import { PDFViewer } from "@react-pdf/renderer";
import { useT } from "@transifex/react";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import useSchoolClassPage from "../../hooks/useSchoolClassPage";
import { getClassDetailsRoute, getDownloadCodesRoute } from "../../helpers/navigationHelper";
import { isPrivateSchoolClass } from "../../utils/schoolClassUtils";
import { getMembersForClass } from "../../networking/schoolClasses";
import type { DownloadCodesClassMemberWithQR } from "../../utils/qrCodeUtils";
import generateQRCodes from "../../utils/qrCodeUtils";
import DownloadCodesPDFDocument from "./downloadCodesPdf/DownloadCodesPDFDocument";
import SchoolClassPageLayout from "./layout/SchoolClassPageLayout";

const DownloadCodes = () => {
    const history = useHistory();
    const t = useT();
    const { schoolClassId } = useParams<{ schoolClassId: string }>();

    const { isLoaded, schoolClass } = useSchoolClassPage(getDownloadCodesRoute(schoolClassId), schoolClassId);

    const [classMembersWithQR, setClassMembersWithQR] = useState<DownloadCodesClassMemberWithQR[] | null>(null);
    const [hasError, setHasError] = useState(false);

    // "Download codes" is rendered private-only, but the URL is reachable directly — so the page needs
    // the same rule as the button. A resolved-but-not-private class belongs on its details page; an
    // unknown token is left to useSchoolClassPage's redirect to the class list.
    const isPrivateClass = isPrivateSchoolClass(schoolClass?.classVisibility);
    const isPublicClass = !!schoolClass && !isPrivateClass;

    useEffect(() => {
        if (!isLoaded || !isPublicClass) return;
        history.replace(getClassDetailsRoute(schoolClassId));
    }, [isLoaded, isPublicClass, schoolClassId, history]);

    useEffect(() => {
        // Wait for the class to resolve into a private one: anything else is being redirected away, so
        // fetching earlier only burns 404s.
        if (!isLoaded || !isPrivateClass) return;

        setClassMembersWithQR(null);
        setHasError(false);

        let cancelled = false;
        getMembersForClass(schoolClassId)
            .then((members) =>
                generateQRCodes(
                    members.map((m) => ({ name: m.name, loginCode: m.loginCode })),
                    schoolClassId
                )
            )
            .then((result) => {
                if (!cancelled) setClassMembersWithQR(result);
            })
            .catch((err) => {
                console.error("Failed to load class members for codes PDF", err);
                if (!cancelled) setHasError(true);
            });

        return () => {
            cancelled = true;
        };
    }, [isLoaded, isPrivateClass, schoolClassId]);

    const t_accessData = t("Access codes", {
        _tags: "SchoolClasses, DownloadCodes",
    });
    const t_backToClass = t("Back to class", {
        _tags: "SchoolClasses, DownloadCodes",
    });
    const t_loading = t("Creating PDF...", {
        _tags: "SchoolClasses, DownloadCodes",
    });
    const t_error = t("Error creating PDF. Please reload the page.", {
        _tags: "SchoolClasses, DownloadCodes",
    });
    const t_emptyStateTitle = t("No students yet.", {
        _tags: "SchoolClasses, DownloadCodes",
    });
    const t_emptyStateDescription = t(
        "Once you have added your first students, the access information for printing will appear here.",
        {
            _tags: "SchoolClasses, DownloadCodes",
        }
    );
    const t_classCode = t("Class Code", { _tags: "SchoolClasses, DownloadCodes" });
    const t_personalCode = t("Personal Code", { _tags: "SchoolClasses, DownloadCodes" });
    const t_classroom = t("Class room", { _tags: "SchoolClasses, DownloadCodes" });
    const t_accessCodes = t("Access codes", { _tags: "SchoolClasses, DownloadCodes" });

    const renderContent = () => {
        if (hasError) {
            return <p className="text-center text-p6-grey-dark">{t_error}</p>;
        }

        if (!isLoaded || !classMembersWithQR) {
            return <p className="text-center text-p6-grey-dark">{t_loading}</p>;
        }

        return (
            <div
                className="w-full flex-1 min-h-0"
                data-testid="download-codes-pdf"
            >
                <PDFViewer style={{ width: "100%", height: "100%" }}>
                    <DownloadCodesPDFDocument
                        classMembers={classMembersWithQR}
                        schoolClassId={schoolClassId}
                        schoolClassName={schoolClass?.name ?? ""}
                        translations={{
                            classCode: t_classCode,
                            personalCode: t_personalCode,
                            classroom: t_classroom,
                            accessCodes: t_accessCodes,
                            emptyStateTitle: t_emptyStateTitle,
                            emptyStateDescription: t_emptyStateDescription,
                        }}
                    />
                </PDFViewer>
            </div>
        );
    };

    return (
        <SchoolClassPageLayout
            backLabel={t_backToClass}
            onBack={() => history.push(getClassDetailsRoute(schoolClassId))}
            rightLabel={t_accessData}
            fullWidth
            noPadding
            data-testid="download-codes-screen"
        >
            {renderContent()}
        </SchoolClassPageLayout>
    );
};

export default DownloadCodes;
